import React, { useState, useEffect, useRef, useCallback } from 'react';
import { FiMenu, FiX } from 'react-icons/fi';
import useFocusTrap from '../hooks/useFocusTrap';

const SECTIONS = ['hero', 'projects', 'skills', 'experience', 'about', 'contact'];
// How far from the top of the viewport a section's top edge must be to count as "active"
const SCROLL_OFFSET = 130;

const Header = ({ onOpenResume }) => {
    const [isScrolled, setIsScrolled] = useState(false);
    const [activeSection, setActiveSection] = useState('hero');
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const navRef = useRef(null);

    useFocusTrap(navRef, isMenuOpen);

    // ── Scrolled state ──────────────────────────────────────────────────────
    useEffect(() => {
        const handleScroll = () => setIsScrolled(window.scrollY > 50);
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // ── Scroll-spy: position-based (reliable for tall sections) ────────────
    useEffect(() => {
        const detect = () => {
            let current = SECTIONS[0];
            for (const id of SECTIONS) {
                const el = document.getElementById(id);
                if (!el) continue;
                // getBoundingClientRect().top is viewport-relative;
                // when the section's top has scrolled above SCROLL_OFFSET it's "in view".
                if (el.getBoundingClientRect().top <= SCROLL_OFFSET) {
                    current = id;
                }
            }
            setActiveSection(current);
        };

        detect(); // run on mount
        window.addEventListener('scroll', detect, { passive: true });
        return () => window.removeEventListener('scroll', detect);
    }, []);

    // ── Body scroll lock when mobile nav is open ────────────────────────────
    useEffect(() => {
        if (isMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }
        return () => { document.body.style.overflow = ''; };
    }, [isMenuOpen]);

    // ── Escape key closes mobile nav ────────────────────────────────────────
    useEffect(() => {
        if (!isMenuOpen) return;
        const handleKeyDown = (e) => {
            if (e.key === 'Escape') setIsMenuOpen(false);
        };
        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [isMenuOpen]);

    const closeMenu = useCallback(() => setIsMenuOpen(false), []);
    const toggleMenu = useCallback(() => setIsMenuOpen(prev => !prev), []);

    const scrollToSection = useCallback((e, id) => {
        e.preventDefault();
        // Clear overflow immediately — do not wait for React's useEffect to run,
        // which may lag behind the 10ms timer on slow devices.
        document.body.style.overflow = '';
        closeMenu();
        // Wait for the nav slide-out animation (400ms) before scrolling,
        // otherwise the scroll fires while the panel is still closing.
        setTimeout(() => {
            const el = document.getElementById(id);
            if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 420);
    }, [closeMenu]);

    const handleHomeClick = useCallback((e) => {
        e.preventDefault();
        document.body.style.overflow = '';
        closeMenu();
        setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 420);
    }, [closeMenu]);

    return (
        <>
            <header className={`header ${isScrolled ? 'scrolled' : ''}`}>
                <div className="logo">Morpheus Vibaen J</div>

                {/* Hamburger button stays inside the header */}
                <button
                    className={`mobile-menu-btn ${isMenuOpen ? 'is-open' : ''}`}
                    onClick={toggleMenu}
                    aria-label={isMenuOpen ? 'Close Navigation' : 'Open Navigation'}
                    aria-expanded={isMenuOpen}
                    aria-controls="main-nav"
                >
                    {isMenuOpen ? <FiX /> : <FiMenu />}
                </button>
            </header>

            {/*
              Nav is rendered OUTSIDE <header> intentionally.
              The header is position:fixed with z-index:1000, forming its own
              stacking context. Any child inside it — regardless of its own
              z-index — is capped at the header's root-level z-index of 1000.
              The backdrop renders at z-index:9998 in the ROOT stacking context,
              which is above 1000, so it would block all tap events on links
              inside the header. Moving nav outside lets it operate at root level
              where z-index:9999 truly sits above the backdrop's 9998.
            */}
            <nav
                id="main-nav"
                className={`nav ${isMenuOpen ? 'open' : ''}`}
                aria-label="Main navigation"
                ref={navRef}
            >
                <a href="#hero" className={activeSection === 'hero' ? 'active' : ''} aria-current={activeSection === 'hero' ? 'page' : undefined} onClick={handleHomeClick}>Home</a>
                <a href="#projects" className={activeSection === 'projects' ? 'active' : ''} aria-current={activeSection === 'projects' ? 'page' : undefined} onClick={(e) => scrollToSection(e, 'projects')}>Projects</a>
                <a href="#skills" className={activeSection === 'skills' ? 'active' : ''} aria-current={activeSection === 'skills' ? 'page' : undefined} onClick={(e) => scrollToSection(e, 'skills')}>Skills</a>
                <a href="#experience" className={activeSection === 'experience' ? 'active' : ''} aria-current={activeSection === 'experience' ? 'page' : undefined} onClick={(e) => scrollToSection(e, 'experience')}>Experience</a>
                <a href="#about" className={activeSection === 'about' ? 'active' : ''} aria-current={activeSection === 'about' ? 'page' : undefined} onClick={(e) => scrollToSection(e, 'about')}>About</a>
                <a href="#contact" className={activeSection === 'contact' ? 'active' : ''} aria-current={activeSection === 'contact' ? 'page' : undefined} onClick={(e) => scrollToSection(e, 'contact')}>Contact</a>
                <a href="#resume" className="nav-resume-cta" onClick={(e) => { e.preventDefault(); closeMenu(); onOpenResume?.(); }}>Resume</a>
            </nav>

            {/* Backdrop — dims page behind nav and closes it when tapped */}
            {isMenuOpen && (
                <div
                    className="nav-backdrop"
                    onClick={closeMenu}
                    aria-hidden="true"
                />
            )}
        </>
    );
};

export default Header;